import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Users, ArrowRightCircle, Target, ChevronRight, Globe } from 'lucide-react';
import { CompletedCompetition } from '../hooks/useRecentlyCompletedCompetitions';

interface RecentlyCompletedSectionProps {
  competitions: CompletedCompetition[];
  organizationSlug: string;
}

const TYPE_META: Record<
  CompletedCompetition['competitionType'],
  {
    label: string;
    routeSegment: string;
    iconBg: string;
    badgeClass: string;
    Icon: typeof Trophy;
  }
> = {
  lms: {
    label: 'LMS',
    routeSegment: 'lms',
    iconBg: 'bg-gradient-to-br from-green-400/80 to-emerald-500/80',
    badgeClass: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    Icon: Target,
  },
  predictor: {
    label: 'Predictor',
    routeSegment: 'predictor',
    iconBg: 'bg-gradient-to-br from-blue-400/80 to-cyan-500/80',
    badgeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    Icon: Trophy,
  },
  worldcup: {
    label: 'World Cup',
    routeSegment: 'worldcup',
    iconBg: 'bg-gradient-to-br from-amber-400/80 to-orange-500/80',
    badgeClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    Icon: Globe,
  },
};

export function RecentlyCompletedSection({
  competitions,
  organizationSlug,
}: RecentlyCompletedSectionProps) {
  const navigate = useNavigate();

  if (competitions.length === 0) {
    return null;
  }

  const handleCompetitionClick = (competition: CompletedCompetition) => {
    const { routeSegment } = TYPE_META[competition.competitionType];
    navigate(`/${organizationSlug}/${routeSegment}?comp=${competition.id}`);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getCompletionLabel = (
    completionType: CompletedCompetition['completionType'],
    winnerCount: number,
    competitionType: CompletedCompetition['competitionType'],
  ) => {
    switch (completionType) {
      case 'single_winner':
        return 'Winner';
      case 'pot_split':
        return `Pot Split - ${winnerCount} Winner${winnerCount !== 1 ? 's' : ''}`;
      case 'rollover':
        return 'Rolled Over';
      default:
        if (competitionType === 'worldcup') {
          return 'Completed';
        }
        return winnerCount > 1 ? `${winnerCount} Winners` : 'Completed';
    }
  };

  const getCompletionIcon = (completionType: CompletedCompetition['completionType']) => {
    switch (completionType) {
      case 'pot_split':
        return <Users className="w-4 h-4" />;
      case 'rollover':
        return <ArrowRightCircle className="w-4 h-4" />;
      default:
        return <Trophy className="w-4 h-4" />;
    }
  };

  return (
    <section className="mt-8">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" />
        Recently Completed
      </h2>

      <div className="space-y-3">
        {competitions.map((competition) => {
          const typeMeta = TYPE_META[competition.competitionType];
          const TypeIcon = typeMeta.Icon;

          return (
            <button
              key={competition.id}
              onClick={() => handleCompetitionClick(competition)}
              className="w-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/80 dark:to-gray-800/60 rounded-xl p-4 sm:p-5 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md transition-all group text-left"
            >
              <div className="flex items-start gap-3 sm:gap-4">
                <div
                  className={`inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex-shrink-0 shadow-sm ${typeMeta.iconBg}`}
                >
                  <TypeIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-base sm:text-lg text-gray-900 dark:text-white truncate">
                      {competition.name}
                    </h3>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${typeMeta.badgeClass}`}
                    >
                      {typeMeta.label}
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                      {getCompletionIcon(competition.completionType)}
                      {getCompletionLabel(
                        competition.completionType,
                        competition.winners.length,
                        competition.competitionType,
                      )}
                    </span>
                  </div>

                  {competition.winners.length > 0 &&
                    competition.completionType !== 'rollover' &&
                    competition.winners.some(
                      (w) => w.firstName !== 'Unknown' || w.lastName !== 'User',
                    ) && (
                      <div className="mb-2">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {competition.winners
                            .filter(
                              (winner) =>
                                winner.firstName !== 'Unknown' || winner.lastName !== 'User',
                            )
                            .map((winner, index, filteredWinners) => (
                              <span key={winner.id}>
                                <span className="font-medium">
                                  {winner.firstName} {winner.lastName}
                                </span>
                                {index < filteredWinners.length - 1 && (
                                  <span className="text-gray-400 dark:text-gray-500"> • </span>
                                )}
                              </span>
                            ))}
                        </p>
                      </div>
                    )}

                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {competition.competitionType !== 'worldcup' &&
                      competition.finalGameweek &&
                      (competition.completionType === 'rollover'
                        ? `Rolled over after Gameweek ${competition.finalGameweek} • `
                        : `Completed after Gameweek ${competition.finalGameweek} • `)}
                    {formatDate(competition.completedAt)}
                  </p>
                </div>

                <div className="flex-shrink-0 flex items-center self-center">
                  <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
